import type {
  AffiliateDto,
  BroadcastSegment,
  CommissionDto,
  PromotionDto,
  UpsertPromotionInput,
} from '@telestore/contracts';
import { COMMISSION_RETURN_WINDOW_DAYS } from '@telestore/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import {
  Button,
  CopyButton,
  Empty,
  ErrorBox,
  Field,
  Input,
  Money,
  Panel,
  Pill,
  Row,
  Rows,
  Select,
  Spinner,
  Textarea,
} from '../components/ui.js';

/**
 * Marketing (CLAUDE.md §3: `promotion`, `affiliate`, `automation`).
 *
 * Five surfaces a merchant reaches for in the same mood — "how do I get more
 * orders this week" — so they share one screen with tabs rather than five
 * entries competing for space in a bottom nav used one-handed.
 *
 * Everything here is entitlement-gated server-side. When a plan does not
 * include a feature the API returns a plan_required error, and the tab shows
 * that message rather than pretending the feature is broken.
 */

type Tab = 'promotions' | 'affiliates' | 'broadcasts' | 'automation' | 'assistant';

export function MarketingPage() {
  const { t } = useSession();
  const [tab, setTab] = useState<Tab>('promotions');

  const TABS: Array<{ id: Tab; label: string }> = [
    { id: 'promotions', label: t('growth.promotions') },
    { id: 'affiliates', label: t('growth.affiliates') },
    { id: 'broadcasts', label: t('growth.broadcasts') },
    { id: 'automation', label: t('growth.automation') },
    { id: 'assistant', label: t('growth.assistant') },
  ];

  return (
    <div className="flex flex-col gap-4">
      <nav className="flex gap-1 overflow-x-auto">
        {TABS.map((entry) => (
          <Button
            key={entry.id}
            variant="ghost"
            size="sm"
            onClick={() => setTab(entry.id)}
            className={
              tab === entry.id
                ? 'min-h-9 rounded-full bg-[var(--color-brand-soft)] text-[var(--color-brand)]'
                : 'min-h-9 rounded-full text-muted-foreground'
            }
          >
            {entry.label}
          </Button>
        ))}
      </nav>

      {tab === 'promotions' ? <Promotions /> : null}
      {tab === 'affiliates' ? <Affiliates /> : null}
      {tab === 'broadcasts' ? <Broadcasts /> : null}
      {tab === 'automation' ? <Automation /> : null}
      {tab === 'assistant' ? <Assistant /> : null}
    </div>
  );
}

/** A locked feature is a sales moment, not an error (§10). */
function GateOrError({ error }: { error: unknown }) {
  const { t } = useSession();
  if (error instanceof ApiError && error.code === 'plan_required') {
    return <Empty title={t('admin.upgradeToUnlock')} hint={error.message} />;
  }
  return <ErrorBox message={error instanceof ApiError ? error.message : t('errors.internal')} />;
}

/* ------------------------------------------------------------------ */
/* Promotions                                                          */
/* ------------------------------------------------------------------ */

const BLANK_PROMOTION: UpsertPromotionInput = {
  name: { km: '', en: '' },
  code: null,
  condition: {
    scope: 'order',
    productIds: [],
    categoryIds: [],
    minSubtotal: null,
    startsAt: null,
    endsAt: null,
    customerSegment: 'all',
    usageLimitTotal: null,
    usageLimitPerCustomer: null,
  },
  effect: { kind: 'percent_off', value: 10, maxDiscount: null },
  isActive: true,
  priority: 0,
};

function Promotions() {
  const { shopId, locale, t } = useSession();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<UpsertPromotionInput | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const promotions = useQuery({
    queryKey: ['promotions', shopId],
    queryFn: () => api.promotions(shopId!),
    enabled: Boolean(shopId),
    retry: false,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['promotions', shopId] });
    setDraft(null);
    setEditing(null);
    setError(null);
  };

  const save = useMutation({
    mutationFn: (input: UpsertPromotionInput) =>
      editing
        ? api.updatePromotion(shopId!, editing, input)
        : api.createPromotion(shopId!, input),
    onSuccess: invalidate,
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : t('errors.internal')),
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => api.deletePromotion(shopId!, id),
    onSuccess: invalidate,
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : t('errors.internal')),
  });

  if (promotions.isPending) return <Spinner label={t('common.loading')} />;
  if (promotions.isError) return <GateOrError error={promotions.error} />;

  const rows = promotions.data.promotions;

  return (
    <div className="flex flex-col gap-4">
      <Panel
        title={t('growth.promotions')}
        action={
          <Button onClick={() => { setEditing(null); setDraft(BLANK_PROMOTION); }} >
            {t('growth.newPromotion')}
          </Button>
        }
        padded={false}
      >
        <p className="px-4 py-3 text-xs" style={{ color: 'var(--color-ink-faint)' }}>
          {t('growth.stackingNote')}
        </p>

        {rows.length === 0 ? (
          <Empty title={t('growth.noPromotions')} hint={t('growth.noPromotionsHint')} />
        ) : (
          <Rows>
            {rows.map((promotion) => (
              <Row key={promotion.id}>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">
                    {promotion.name[locale] || promotion.name.en}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--color-ink-soft)' }}>
                    {describeEffect(promotion, t)}
                    {promotion.code ? ` · ${promotion.code}` : ''}
                    {' · '}
                    {t('growth.redemptions', { count: promotion.redemptionCount })}
                  </p>
                </div>

                <Pill tone={promotion.isActive ? 'positive' : 'neutral'}>
                  {promotion.isActive ? t('growth.active') : t('growth.paused')}
                </Pill>

                {promotion.shareUrl ? (
                  <CopyButton
                    value={promotion.shareUrl}
                    label={t('growth.copyShareLink')}
                    copiedLabel={t('common.copied')}
                  />
                ) : null}

                <Button variant="outline" onClick={() => { setEditing(promotion.id); setDraft(toInput(promotion)); }} >
                  {t('common.edit')}
                </Button>

                {promotion.isActive ? (
                  <Button variant="outline" onClick={() => deactivate.mutate(promotion.id)} >
                    {t('growth.deactivate')}
                  </Button>
                ) : null}
              </Row>
            ))}
          </Rows>
        )}
      </Panel>

      {draft ? (
        <PromotionForm
          value={draft}
          onChange={setDraft}
          onCancel={() => {
            setDraft(null);
            setEditing(null);
          }}
          onSave={() => save.mutate(draft)}
          saving={save.isPending}
        />
      ) : null}

      {error ? <ErrorBox message={error} /> : null}
    </div>
  );
}

function toInput(promotion: PromotionDto): UpsertPromotionInput {
  return {
    name: promotion.name,
    code: promotion.code,
    condition: promotion.condition,
    effect: promotion.effect,
    isActive: promotion.isActive,
    priority: promotion.priority,
  };
}

function describeEffect(
  promotion: Pick<PromotionDto, 'effect'>,
  t: ReturnType<typeof useSession>['t'],
): string {
  const { kind, value } = promotion.effect;
  if (kind === 'free_shipping') return t('growth.freeShipping');
  if (kind === 'percent_off') return `−${value}%`;
  return `−${value / 100}`;
}

function PromotionForm({
  value,
  onChange,
  onCancel,
  onSave,
  saving,
}: {
  value: UpsertPromotionInput;
  onChange: (next: UpsertPromotionInput) => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
}) {
  const { t } = useSession();
  const patch = (partial: Partial<UpsertPromotionInput>) => onChange({ ...value, ...partial });

  return (
    <Panel title={t('growth.newPromotion')}>
      <div className="flex flex-col gap-3">
        <Field label={`${t('growth.promoName')} (EN)`}>
          <Input
 
            value={value.name.en}
            onChange={(event) => patch({ name: { ...value.name, en: event.target.value } })}
          />
        </Field>
        <Field label={`${t('growth.promoName')} (ខ្មែរ)`}>
          <Input
 
            value={value.name.km}
            onChange={(event) => patch({ name: { ...value.name, km: event.target.value } })}
          />
        </Field>

        <Field label={t('growth.promoCodeOptional')}>
          <Input
            className="uppercase"
            value={value.code ?? ''}
            onChange={(event) => patch({ code: event.target.value.trim() || null })}
            autoCapitalize="characters"
            spellCheck={false}
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t('growth.discountType')}>
            <Select
 
              value={value.effect.kind}
              onChange={(event) =>
              patch({
              effect: {
              ...value.effect,
              kind: event.target.value as UpsertPromotionInput['effect']['kind'],
              },
              })
              }
            >
              <option value="percent_off">{t('growth.percentOff')}</option>
              <option value="fixed_off">{t('growth.fixedOff')}</option>
              <option value="free_shipping">{t('growth.freeShipping')}</option>
            </Select>
          </Field>

          {value.effect.kind === 'free_shipping' ? null : (
            <Field label={t('growth.discountValue')}>
              <Input
 
                type="number"
                min={0}
                value={value.effect.value}
                onChange={(event) =>
                patch({ effect: { ...value.effect, value: Number(event.target.value) } })
                }
              />
            </Field>
          )}
        </div>

        {value.effect.kind === 'percent_off' ? (
          <Field
            label={t('growth.maxDiscount')}
            hint="0 = no cap"
          >
            <Input
 
              type="number"
              min={0}
              value={value.effect.maxDiscount ?? 0}
              onChange={(event) =>
              patch({
              effect: { ...value.effect, maxDiscount: Number(event.target.value) || null },
              })
              }
            />
          </Field>
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          <Field label={t('growth.minSubtotal')}>
            <Input
 
              type="number"
              min={0}
              value={value.condition.minSubtotal ?? 0}
              onChange={(event) =>
              patch({
              condition: {
              ...value.condition,
              minSubtotal: Number(event.target.value) || null,
              },
              })
              }
            />
          </Field>
          <Field label={t('growth.customerSegment')}>
            <Select
 
              value={value.condition.customerSegment}
              onChange={(event) =>
              patch({
              condition: {
              ...value.condition,
              customerSegment: event.target
              .value as UpsertPromotionInput['condition']['customerSegment'],
              },
              })
              }
            >
              <option value="all">{t('growth.segmentAll')}</option>
              <option value="new">{t('growth.segmentNew')}</option>
              <option value="returning">{t('growth.segmentReturning')}</option>
            </Select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t('growth.startsAt')}>
            <Input
 
              type="datetime-local"
              value={toLocalInput(value.condition.startsAt)}
              onChange={(event) =>
              patch({
              condition: { ...value.condition, startsAt: fromLocalInput(event.target.value) },
              })
              }
            />
          </Field>
          <Field label={t('growth.endsAt')}>
            <Input
 
              type="datetime-local"
              value={toLocalInput(value.condition.endsAt)}
              onChange={(event) =>
              patch({
              condition: { ...value.condition, endsAt: fromLocalInput(event.target.value) },
              })
              }
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t('growth.usageLimitTotal')}>
            <Input
 
              type="number"
              min={0}
              value={value.condition.usageLimitTotal ?? 0}
              onChange={(event) =>
              patch({
              condition: {
              ...value.condition,
              usageLimitTotal: Number(event.target.value) || null,
              },
              })
              }
            />
          </Field>
          <Field label={t('growth.usageLimitPerCustomer')}>
            <Input
 
              type="number"
              min={0}
              value={value.condition.usageLimitPerCustomer ?? 0}
              onChange={(event) =>
              patch({
              condition: {
              ...value.condition,
              usageLimitPerCustomer: Number(event.target.value) || null,
              },
              })
              }
            />
          </Field>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={value.isActive}
            onChange={(event) => patch({ isActive: event.target.checked })}
          />
          {t('growth.active')}
        </label>

        <div className="flex gap-2">
          <Button disabled={saving || value.name.en.trim().length < 2} onClick={onSave} >
            {saving ? t('common.loading') : t('common.save')}
          </Button>
          <Button variant="outline" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    </Panel>
  );
}

/**
 * `datetime-local` speaks wall clock. Cambodia is a single timezone (§6.5), so
 * the conversion is a plain slice with no offset arithmetic to get wrong.
 */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toISOString().slice(0, 16);
}

function fromLocalInput(value: string): string | null {
  if (!value) return null;
  return new Date(value).toISOString();
}

/* ------------------------------------------------------------------ */
/* Affiliates                                                          */
/* ------------------------------------------------------------------ */

function Affiliates() {
  const { shopId, t } = useSession();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [percent, setPercent] = useState(5);
  const [error, setError] = useState<string | null>(null);

  const affiliates = useQuery({
    queryKey: ['affiliates', shopId],
    queryFn: () => api.affiliates(shopId!),
    enabled: Boolean(shopId),
    retry: false,
  });

  const commissions = useQuery({
    queryKey: ['commissions', shopId, 'approved'],
    queryFn: () => api.commissions(shopId!, { status: 'approved' }),
    enabled: Boolean(shopId) && affiliates.isSuccess,
    retry: false,
  });

  const create = useMutation({
    mutationFn: () =>
      api.createAffiliate(shopId!, {
        name: name.trim(),
        code: code.trim().toUpperCase(),
        // Basis points keep commission maths in integers (§7 testing floor).
        commissionBps: Math.round(percent * 100),
        minPayoutAmount: 0,
        status: 'active',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['affiliates', shopId] });
      setCreating(false);
      setName('');
      setCode('');
      setError(null);
    },
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : t('errors.internal')),
  });

  if (affiliates.isPending) return <Spinner label={t('common.loading')} />;
  if (affiliates.isError) return <GateOrError error={affiliates.error} />;

  return (
    <div className="flex flex-col gap-4">
      <Panel
        title={t('growth.affiliates')}
        action={
          <Button onClick={() => setCreating(true)}>
            {t('growth.newAffiliate')}
          </Button>
        }
        padded={false}
      >
        <p className="px-4 py-3 text-xs" style={{ color: 'var(--color-ink-faint)' }}>
          {t('growth.returnWindowNote', { days: COMMISSION_RETURN_WINDOW_DAYS })}
        </p>

        {affiliates.data.affiliates.length === 0 ? (
          <Empty title={t('growth.noAffiliates')} hint={t('growth.noAffiliatesHint')} />
        ) : (
          <Rows>
            {affiliates.data.affiliates.map((affiliate) => (
              <AffiliateRow key={affiliate.id} affiliate={affiliate} />
            ))}
          </Rows>
        )}
      </Panel>

      {creating ? (
        <Panel title={t('growth.newAffiliate')}>
          <div className="flex flex-col gap-3">
            <Field label={t('growth.affiliateName')}>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label={t('growth.affiliateCode')}>
              <Input
                className="uppercase"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoCapitalize="characters"
                spellCheck={false}
              />
            </Field>
            <Field label={`${t('growth.commissionRate')} (%)`}>
              <Input
 
                type="number"
                min={0}
                max={50}
                step={0.5}
                value={percent}
                onChange={(e) => setPercent(Number(e.target.value))}
              />
            </Field>
            <div className="flex gap-2">
              <Button disabled={create.isPending || name.trim().length < 2 || code.trim().length < 3} onClick={() => create.mutate()} >
                {create.isPending ? t('common.loading') : t('common.save')}
              </Button>
              <Button variant="outline" onClick={() => setCreating(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        </Panel>
      ) : null}

      {commissions.isSuccess && commissions.data.commissions.length > 0 ? (
        <PayoutPanel commissions={commissions.data.commissions} />
      ) : null}

      {error ? <ErrorBox message={error} /> : null}
    </div>
  );
}

function AffiliateRow({ affiliate }: { affiliate: AffiliateDto }) {
  const { t } = useSession();
  return (
    <Row>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{affiliate.name}</p>
        <p className="text-xs" style={{ color: 'var(--color-ink-soft)' }}>
          {affiliate.code} · {affiliate.commissionBps / 100}% ·{' '}
          {affiliate.stats.orders} {t('admin.orders').toLowerCase()}
        </p>
      </div>
      <div className="text-right text-xs">
        <p style={{ color: 'var(--color-ink-soft)' }}>
          {t('growth.commissionPending')}{' '}
          <Money
            amount={affiliate.stats.pending.amount}
            currency={affiliate.stats.pending.currency}
          />
        </p>
        <p style={{ color: 'var(--color-positive)' }}>
          {t('growth.commissionApproved')}{' '}
          <Money
            amount={affiliate.stats.approved.amount}
            currency={affiliate.stats.approved.currency}
          />
        </p>
      </div>
      <CopyButton
        value={affiliate.shareUrl}
        label={t('growth.referralLink')}
        copiedLabel={t('common.copied')}
      />
    </Row>
  );
}

/**
 * §3 pays affiliates by KHQR transfer, so the merchant does the transfer in
 * their bank app and records it here. We never move money.
 */
function PayoutPanel({ commissions }: { commissions: CommissionDto[] }) {
  const { shopId, t } = useSession();
  const queryClient = useQueryClient();
  const [reference, setReference] = useState('');
  const [error, setError] = useState<string | null>(null);

  const byAffiliate = new Map<string, CommissionDto[]>();
  for (const commission of commissions) {
    const list = byAffiliate.get(commission.affiliateId) ?? [];
    list.push(commission);
    byAffiliate.set(commission.affiliateId, list);
  }

  const pay = useMutation({
    mutationFn: (affiliateId: string) =>
      api.payCommissions(shopId!, {
        affiliateId,
        commissionIds: (byAffiliate.get(affiliateId) ?? []).map((entry) => entry.id),
        reference: reference.trim() || 'manual',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['commissions', shopId] });
      void queryClient.invalidateQueries({ queryKey: ['affiliates', shopId] });
      setReference('');
      setError(null);
    },
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : t('errors.internal')),
  });

  return (
    <Panel title={t('growth.commissionApproved')}>
      <Field label={t('growth.payoutReference')}>
        <Input
 
          value={reference}
          onChange={(event) => setReference(event.target.value)}
        />
      </Field>

      <div className="mt-3 flex flex-col gap-2">
        {[...byAffiliate.entries()].map(([affiliateId, entries]) => {
          const total = entries.reduce((sum, entry) => sum + entry.amount.amount, 0);
          return (
            <div key={affiliateId} className="flex items-center gap-3">
              <span className="flex-1 truncate text-sm">{entries[0]!.affiliateName}</span>
              <Money
                className="font-semibold tabular-nums"
                amount={total}
                currency={entries[0]!.amount.currency}
              />
              <Button disabled={pay.isPending} onClick={() => pay.mutate(affiliateId)} >
                {t('growth.payOut')}
              </Button>
            </div>
          );
        })}
      </div>

      {error ? <div className="mt-3"><ErrorBox message={error} /></div> : null}
    </Panel>
  );
}

/* ------------------------------------------------------------------ */
/* Broadcasts                                                          */
/* ------------------------------------------------------------------ */

const SEGMENTS: BroadcastSegment[] = [
  'all',
  'with_orders',
  'no_orders',
  'recent_60d',
  'dormant_60d',
];

function Broadcasts() {
  const { shopId, locale, t } = useSession();
  const queryClient = useQueryClient();
  const [composing, setComposing] = useState(false);
  const [name, setName] = useState('');
  const [body, setBody] = useState({ km: '', en: '' });
  const [segment, setSegment] = useState<BroadcastSegment>('all');
  const [error, setError] = useState<string | null>(null);

  const broadcasts = useQuery({
    queryKey: ['broadcasts', shopId],
    queryFn: () => api.broadcasts(shopId!),
    enabled: Boolean(shopId),
    retry: false,
    // A run in flight updates its counters as batches drain.
    refetchInterval: (query) =>
      query.state.data?.broadcasts.some((entry) =>
        ['queued', 'sending'].includes(entry.status),
      )
        ? 4000
        : false,
  });

  const preview = useQuery({
    queryKey: ['broadcast-preview', shopId, segment],
    queryFn: () => api.previewSegment(shopId!, segment),
    enabled: Boolean(shopId) && composing,
    retry: false,
  });

  const create = useMutation({
    mutationFn: () => api.createBroadcast(shopId!, { name: name.trim(), body, segment }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['broadcasts', shopId] });
      setComposing(false);
      setName('');
      setBody({ km: '', en: '' });
      setError(null);
    },
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : t('errors.internal')),
  });

  const send = useMutation({
    mutationFn: (id: string) => api.sendBroadcast(shopId!, id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['broadcasts', shopId] }),
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : t('errors.internal')),
  });

  const cancel = useMutation({
    mutationFn: (id: string) => api.cancelBroadcast(shopId!, id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['broadcasts', shopId] }),
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : t('errors.internal')),
  });

  if (broadcasts.isPending) return <Spinner label={t('common.loading')} />;
  if (broadcasts.isError) return <GateOrError error={broadcasts.error} />;

  const segmentLabel = (value: BroadcastSegment) =>
    ({
      all: t('common.all'),
      with_orders: t('growth.segmentWithOrders'),
      no_orders: t('growth.segmentNoOrders'),
      recent_60d: t('growth.segmentRecent'),
      dormant_60d: t('growth.segmentDormant'),
    })[value];

  return (
    <div className="flex flex-col gap-4">
      <Panel
        title={t('growth.broadcasts')}
        action={
          <Button onClick={() => setComposing(true)}>
            {t('growth.newBroadcast')}
          </Button>
        }
        padded={false}
      >
        <p className="px-4 py-3 text-xs" style={{ color: 'var(--color-ink-faint)' }}>
          {t('growth.pacedNote')}
        </p>

        {broadcasts.data.broadcasts.length === 0 ? (
          <Empty title={t('growth.noBroadcasts')} />
        ) : (
          <Rows>
            {broadcasts.data.broadcasts.map((broadcast) => (
              <Row key={broadcast.id}>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{broadcast.name}</p>
                  <p className="truncate text-xs" style={{ color: 'var(--color-ink-soft)' }}>
                    {segmentLabel(broadcast.segment)} ·{' '}
                    {broadcast.status === 'draft'
                      ? t('growth.recipients', { count: broadcast.recipientCount })
                      : t('growth.sentSummary', {
                          sent: broadcast.sentCount,
                          blocked: broadcast.blockedCount,
                          failed: broadcast.failedCount,
                        })}
                  </p>
                </div>

                <Pill
                  tone={
                    broadcast.status === 'sent'
                      ? 'positive'
                      : broadcast.status === 'sending' || broadcast.status === 'queued'
                        ? 'brand'
                        : broadcast.status === 'failed'
                          ? 'danger'
                          : 'neutral'
                  }
                >
                  {broadcast.status}
                </Pill>

                {broadcast.status === 'draft' ? (
                  <Button disabled={send.isPending} onClick={() => send.mutate(broadcast.id)} >
                    {t('growth.sendNow')}
                  </Button>
                ) : null}

                {broadcast.status === 'queued' || broadcast.status === 'sending' ? (
                  <Button variant="outline" onClick={() => cancel.mutate(broadcast.id)} >
                    {t('growth.cancelBroadcast')}
                  </Button>
                ) : null}
              </Row>
            ))}
          </Rows>
        )}
      </Panel>

      {composing ? (
        <Panel title={t('growth.newBroadcast')}>
          <div className="flex flex-col gap-3">
            <Field label={t('growth.broadcastName')}>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label={`${t('growth.broadcastBody')} (ខ្មែរ)`}>
              <Textarea
 
                rows={3}
                value={body.km}
                onChange={(e) => setBody({ ...body, km: e.target.value })}
              />
            </Field>
            <Field label={`${t('growth.broadcastBody')} (EN)`}>
              <Textarea
 
                rows={3}
                value={body.en}
                onChange={(e) => setBody({ ...body, en: e.target.value })}
              />
            </Field>
            <Field
              label={t('growth.audience')}
              hint={
                preview.isSuccess
                  ? t('growth.recipients', { count: preview.data.recipients })
                  : undefined
              }
            >
              <Select
 
                value={segment}
                onChange={(e) => setSegment(e.target.value as BroadcastSegment)}
              >
                {SEGMENTS.map((value) => (
                  <option key={value} value={value}>
                    {segmentLabel(value)}
                  </option>
                ))}
              </Select>
            </Field>

            <div className="flex gap-2">
              <Button disabled={ create.isPending || name.trim().length < 2 || (body[locale].trim().length < 2 && body.en.trim().length < 2) } onClick={() => create.mutate()} >
                {create.isPending ? t('common.loading') : t('common.save')}
              </Button>
              <Button variant="outline" onClick={() => setComposing(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        </Panel>
      ) : null}

      {error ? <ErrorBox message={error} /> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Automation                                                          */
/* ------------------------------------------------------------------ */

function Automation() {
  const { shopId, t } = useSession();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const settings = useQuery({
    queryKey: ['automation', shopId],
    queryFn: () => api.automation(shopId!),
    enabled: Boolean(shopId),
    retry: false,
  });

  const save = useMutation({
    mutationFn: (patch: Record<string, unknown>) => api.saveAutomation(shopId!, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['automation', shopId] });
      setError(null);
    },
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : t('errors.internal')),
  });

  if (settings.isPending) return <Spinner label={t('common.loading')} />;
  if (settings.isError) return <GateOrError error={settings.error} />;

  const value = settings.data.settings;

  return (
    <div className="flex flex-col gap-4">
      <Panel title={t('growth.automation')}>
        <div className="flex flex-col gap-4">
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={value.paymentReminderEnabled}
              onChange={(event) => save.mutate({ paymentReminderEnabled: event.target.checked })}
            />
            {t('growth.paymentReminder')}
          </label>

          {value.paymentReminderEnabled ? (
            <Field label={t('growth.reminderMinutes')}>
              <Input
 
                type="number"
                min={5}
                max={240}
                defaultValue={value.paymentReminderMinutesBefore}
                onBlur={(event) =>
                save.mutate({ paymentReminderMinutesBefore: Number(event.target.value) })
                }
              />
            </Field>
          ) : null}

          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={value.winBackEnabled}
              onChange={(event) => save.mutate({ winBackEnabled: event.target.checked })}
            />
            {t('growth.winBack')}
          </label>

          {value.winBackEnabled ? (
            <Field label={t('growth.winBackDays')}>
              <Input
 
                type="number"
                min={7}
                max={365}
                defaultValue={value.winBackAfterDays}
                onBlur={(event) => save.mutate({ winBackAfterDays: Number(event.target.value) })}
              />
            </Field>
          ) : null}

          <div className="grid grid-cols-2 gap-3">
            <Field label={`${t('growth.quietHours')} — ${t('growth.startsAt')}`}>
              <Input
 
                type="time"
                defaultValue={value.quietHoursStart}
                onBlur={(event) => save.mutate({ quietHoursStart: event.target.value })}
              />
            </Field>
            <Field label={`${t('growth.quietHours')} — ${t('growth.endsAt')}`}>
              <Input
 
                type="time"
                defaultValue={value.quietHoursEnd}
                onBlur={(event) => save.mutate({ quietHoursEnd: event.target.value })}
              />
            </Field>
          </div>
          <p className="text-xs" style={{ color: 'var(--color-ink-faint)' }}>
            {t('growth.quietHoursHint')}
          </p>
        </div>
      </Panel>

      {error ? <ErrorBox message={error} /> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Assistant (Telegram Business Chat Automation)                       */
/* ------------------------------------------------------------------ */

/**
 * §3 `assistant`. The connection itself is made by the merchant inside their
 * own Telegram settings — we cannot do it for them and must not pretend to, so
 * this panel shows the path, the current state, and the one switch that is
 * genuinely ours: whether the bot drafts or replies.
 */
function Assistant() {
  const { shopId, t } = useSession();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const assistant = useQuery({
    queryKey: ['assistant', shopId],
    queryFn: () => api.assistant(shopId!),
    enabled: Boolean(shopId),
    retry: false,
  });

  const setMode = useMutation({
    mutationFn: (mode: 'draft' | 'auto') => api.setAssistantMode(shopId!, mode),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['assistant', shopId] });
      setError(null);
    },
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : t('errors.internal')),
  });

  if (assistant.isPending) return <Spinner label={t('common.loading')} />;
  if (assistant.isError) return <GateOrError error={assistant.error} />;

  const { status, botUsername } = assistant.data;

  return (
    <div className="flex flex-col gap-4">
      <Panel title={t('growth.assistant')}>
        <p className="text-sm" style={{ color: 'var(--color-ink-soft)' }}>
          {t('growth.assistantIntro')}
        </p>

        <div className="mt-3 flex items-center gap-2">
          <Pill tone={status.connected && status.canReply ? 'positive' : 'warn'}>
            {!status.connected
              ? t('growth.assistantNotConnected')
              : status.canReply
                ? t('growth.assistantConnected')
                : t('growth.assistantNoReply')}
          </Pill>
        </div>

        {!status.connected || !status.canReply ? (
          <p className="mt-3 text-sm">
            {t('growth.assistantSetup', { bot: botUsername ? `@${botUsername}` : 'TeleStore' })}
          </p>
        ) : null}
      </Panel>

      {status.connected ? (
        <Panel title={t('growth.assistantMode')}>
          <div className="flex flex-col gap-2">
            {(['draft', 'auto'] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setMode.mutate(mode)}
                disabled={setMode.isPending}
                className="rounded-[10px] p-3 text-left"
                style={{
                  border: `1px solid ${status.mode === mode ? 'var(--color-brand)' : 'var(--color-line)'}`,
                  background: status.mode === mode ? 'var(--color-brand-soft)' : 'transparent',
                }}
              >
                {mode === 'draft' ? t('growth.assistantModeDraft') : t('growth.assistantModeAuto')}
              </button>
            ))}
          </div>
          <p className="mt-3 text-xs" style={{ color: 'var(--color-ink-faint)' }}>
            {t('growth.assistantTakeover')}
          </p>
        </Panel>
      ) : null}

      {error ? <ErrorBox message={error} /> : null}
    </div>
  );
}
